import React, { useState } from 'react';

import { API_ENDPOINTS } from '../config/api';
import './SecurityPage.css';

const SecurityPage = () => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const updateCode = (value) => {
    setCode(value.replace(/\D/g, '').slice(0, 6));
    setError('');
    setResult(null);
  };
  const handleCodeChange = (event) => updateCode(event.target.value);
  const pressKey = (key) => {
    if (loading) return;
    updateCode(key === 'clear' ? '' : key === 'backspace' ? code.slice(0, -1) : code + key);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (code.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);

    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/security/verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code })
      });

      const data = await response.json();

      if (response.ok) {
        setResult(data);
        setCode(''); // Clear the input
      } else {
        setError(data.error || 'Verification failed');
      }
    } catch (error) {
      console.error('Error verifying code:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 border border-gray-200">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl text-white">Security</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">School Security</h1>
          <p className="text-gray-600">Enter the 6-digit code from your child's student dashboard</p>
        </div>

        {/* Code Input Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-2">
              Security Code
            </label>
            <input
              type="text"
              id="code"
              value={code}
              onChange={handleCodeChange}
              placeholder="000000"
              className="w-full text-center text-2xl font-mono tracking-wider px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
              maxLength={6}
              inputMode="numeric"
              pattern="[0-9]{6}"
              autoComplete="off"
              disabled={loading}
            />
            <p className="text-xs text-gray-500 mt-1 text-center">
              Enter 6 digits (0-9 only)
            </p>
          </div>

          <div className="security-code-keypad" role="group" aria-label="Security code number keypad">
            {['1', '2', '3', '4', '5', '6', '7', '8', '9', 'clear', '0', 'backspace'].map(key => (
              <button
                key={key}
                type="button"
                className={key === 'clear' || key === 'backspace' ? 'security-key-action' : ''}
                aria-label={key === 'clear' ? 'Clear code' : key === 'backspace' ? 'Backspace' : key}
                title={key === 'clear' ? 'Clear code' : key === 'backspace' ? 'Delete last digit' : undefined}
                disabled={loading || (key === 'clear' || key === 'backspace' ? !code.length : code.length === 6)}
                onClick={() => pressKey(key)}
              >
                {key === 'clear' ? 'Clear' : key === 'backspace' ? '\u232b' : key}
              </button>
            ))}
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-center">
              <span className="font-medium">Error:</span> {error}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || code.length !== 6}
            className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Verifying...
              </span>
            ) : (
              'Verify Code'
            )}
          </button>
        </form>

        {/* Success Result */}
        {result && result.success && (
          <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-6">
            <div className="flex items-center justify-center mb-4">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                <span className="text-2xl text-white">?</span>
              </div>
            </div>
            <h3 className="text-lg font-semibold text-green-800 text-center mb-2">
              {result.message}
            </h3>
            <div className="text-center text-sm text-green-700">
              <p className="font-medium">{result.student.name}</p>
              <p>Class: {result.student.class}</p>
              <p>Verified at: {new Date(result.verified_at).toLocaleString()}</p>
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-blue-800 mb-2">How to use:</h4>
          <ul className="text-xs text-blue-700 space-y-1">
            <li>? Ask your child for their security code from their student dashboard</li>
            <li>? Enter the 6-digit code above</li>
            <li>? Submit for dropoff or pickup verification</li>
            <li>? Codes are valid only for today</li>
          </ul>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-xs text-gray-500">
          <p>Creative Town School - Security System</p>
          <p>For support, contact the school administration</p>
        </div>
      </div>
    </div>
  );
};

export default SecurityPage;





